import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { BsBarChartFill, BsPersonFill, BsGraphUpArrow, BsKeyFill, BsKey } from 'react-icons/bs';
import { useParams } from 'react-router-dom';
import './ADSPortal.css';
import axios from 'axios';

const URL_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

type CycleInfo = {
  _id: string;
  name: string;
  clientName: string;
  clientLogo?: string;
  profileModel?: string;
  status: string;
  slug: string;
};

type MappingRecord = {
  _id?: string;
  user?: string;
  profile?: string;
  profileName?: string;
  [key: string]: any;
};

type Session = {
  keyUserName: string;
  keyUserId: string;
};

type MappingResponse = {
  items: MappingRecord[];
  simpleProfilesCatalog?: Array<{
    name: string;
    risk?: boolean;
    master?: boolean;
    derived?: boolean;
    basis?: 'atividade' | 'posicao' | string;
    area?: string;
    process?: string;
    subprocess?: string;
    activity?: string;
    n1?: string;
    n2?: string;
    n3?: string;
    n4?: string;
  }>;
  compositeProfilesCatalog?: string[];
  keyUserName: string;
  responsibleUsers: string[];
  cycleInfo: { name: string; clientName: string; clientLogo?: string; profileModel?: string };
};

const USER_FIELD_CANDIDATES = ['user', 'username', 'usuario', 'uname', 'bname', 'n1'];
const USER_NAME_FIELD_CANDIDATES = ['usuario_name', 'user_name', 'name', 'nome', 'full_name', 'fullname'];
const PROFILE_SIMPLE_FIELD_CANDIDATES = ['profilename', 'profile', 'profileName', 'perfil', 'role', 'rolename', 'n4'];
const PROFILE_COMPOSITE_FIELD_CANDIDATES = ['perfil_composto', 'profile_composite', 'composite_profile', 'compositeprofile'];

function readField(row: MappingRecord, candidates: string[]): string {
  if (!row || typeof row !== 'object') return '';
  const normalized = new Map<string, unknown>();
  for (const [key, value] of Object.entries(row)) {
    normalized.set(String(key || '').trim().toLowerCase(), value);
  }
  for (const candidate of candidates) {
    const value = normalized.get(candidate.toLowerCase());
    const text = String(value ?? '').trim();
    if (text) return text;
  }
  return '';
}

const ADSPortal: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [cycleInfo, setCycleInfo] = useState<CycleInfo | null>(null);
  const [cycleLoading, setCycleLoading] = useState(true);
  const [cycleError, setCycleError] = useState<string | null>(null);

  const [session, setSession] = useState<Session | null>(null);
  const [loginForm, setLoginForm] = useState({ keyUserId: '', password: '' });
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);

  const [mapping, setMapping] = useState<MappingResponse | null>(null);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingError, setMappingError] = useState<string | null>(null);

  // Load cycle info on mount
  useEffect(() => {
    setCycleLoading(true);
    axios
      .get(`${URL_BASE}/api/ads/portal/${slug}`)
      .then((res) => {
        setCycleInfo(res.data);
      })
      .catch((e) => {
        const msg = e?.response?.data?.message || 'Ciclo não encontrado.';
        setCycleError(msg);
      })
      .finally(() => setCycleLoading(false));
  }, [slug]);

  // Try to restore session on mount
  useEffect(() => {
    axios
      .get(`${URL_BASE}/api/ads/portal/${slug}/mapping`, { withCredentials: true })
      .then((res) => {
        setSession({ keyUserName: res.data.keyUserName, keyUserId: '' });
        setMapping(res.data);
      })
      .catch(() => {
        // Not authenticated yet – show login
      });
  }, [slug]);

  const fetchMapping = useCallback(async () => {
    setMappingLoading(true);
    setMappingError(null);
    try {
      const res = await axios.get(`${URL_BASE}/api/ads/portal/${slug}/mapping`, { withCredentials: true });
      setMapping(res.data);
    } catch (e: any) {
      setMappingError(e?.response?.data?.message || 'Erro ao carregar mapeamento.');
    } finally {
      setMappingLoading(false);
    }
  }, [slug]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setLoginLoading(true);
    try {
      const res = await axios.post(
        `${URL_BASE}/api/ads/portal/${slug}/login`,
        { keyUserId: loginForm.keyUserId, password: loginForm.password },
        { withCredentials: true }
      );
      setSession({ keyUserName: res.data.keyUserName, keyUserId: res.data.keyUserId });
      await fetchMapping();
    } catch (e: any) {
      setLoginError(e?.response?.data?.message || 'Credenciais inválidas.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${URL_BASE}/api/ads/portal/${slug}/logout`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
    setSession(null);
    setMapping(null);
  };

  const kpis = useMemo(() => {
    if (!mapping) {
      return {
        uniqueSimpleProfiles: 0,
        uniqueCompositeProfiles: 0,
        totalProfilesForValidation: 0,
        usersForValidation: 0,
        usersWithMapping: 0,
        progressPercent: 0,
      };
    }

    const simpleProfiles = new Set<string>();
    const compositeProfiles = new Set<string>();
    const usersInMapping = new Set<string>();

    for (const row of mapping.items) {
      const user = readField(row, USER_FIELD_CANDIDATES);
      if (user) usersInMapping.add(user.toUpperCase());

      const simpleProfile = readField(row, PROFILE_SIMPLE_FIELD_CANDIDATES);
      if (simpleProfile) simpleProfiles.add(simpleProfile);

      const compositeProfile = readField(row, PROFILE_COMPOSITE_FIELD_CANDIDATES);
      if (compositeProfile && compositeProfile.toUpperCase() !== 'DIRECT_ATTRIBUTION') compositeProfiles.add(compositeProfile);
    }

    const usersForValidation = mapping.responsibleUsers.length;
    const usersWithMapping = usersForValidation > 0
      ? mapping.responsibleUsers.filter((u) => usersInMapping.has(String(u || '').trim().toUpperCase())).length
      : usersInMapping.size;

    const progressPercent = usersForValidation > 0
      ? Math.round((usersWithMapping / usersForValidation) * 100)
      : (mapping.items.length > 0 ? 100 : 0);

    return {
      uniqueSimpleProfiles: simpleProfiles.size,
      uniqueCompositeProfiles: compositeProfiles.size,
      totalProfilesForValidation: simpleProfiles.size + compositeProfiles.size,
      usersForValidation,
      usersWithMapping,
      progressPercent,
    };
  }, [mapping]);

  const dashboardStats = useMemo(() => {
    if (!mapping) {
      return {
        engagementPercent: 0,
        executionPercent: 0,
        completionPercent: 0,
        pendingUsers: 0,
      };
    }

    const engagementPercent = Math.min(100, Math.round((mapping.items.length / Math.max(1, kpis.usersForValidation || 1)) * 10));
    const executionPercent = kpis.totalProfilesForValidation > 0
      ? Math.round((kpis.uniqueCompositeProfiles / kpis.totalProfilesForValidation) * 100)
      : 0;
    const completionPercent = kpis.progressPercent;
    const pendingUsers = Math.max(0, kpis.usersForValidation - kpis.usersWithMapping);

    return {
      engagementPercent,
      executionPercent,
      completionPercent,
      pendingUsers,
    };
  }, [mapping, kpis]);

  const assignedProfiles = useMemo(() => {
    if (!mapping) return [];
    
    const profileMap = new Map<string, {name: string, type: string, rows: MappingRecord[], risk: boolean, master: boolean, derived: boolean}>();
    
    for (const row of mapping.items) {
      const user = readField(row, USER_FIELD_CANDIDATES);
      if (mapping.responsibleUsers.includes(user)) {
        const simpleProfile = readField(row, PROFILE_SIMPLE_FIELD_CANDIDATES);
        if (simpleProfile) {
          const key = simpleProfile + '::simple';
          if (!profileMap.has(key)) {
            profileMap.set(key, {name: simpleProfile, type: 'simples', rows: [], risk: false, master: false, derived: false});
          }
          const entry = profileMap.get(key)!;
          entry.rows.push(row);
          // Accumulate flags from each row (may be boolean, 'true', or 1)
          const toBool = (v: any) => v === true || v === 'true' || v === 1;
          if (toBool((row as any).risk))    entry.risk    = true;
          if (toBool((row as any).master))  entry.master  = true;
          if (toBool((row as any).derived)) entry.derived = true;
        }
        
        const compositeProfile = readField(row, PROFILE_COMPOSITE_FIELD_CANDIDATES);
        if (compositeProfile && compositeProfile.toUpperCase() !== 'DIRECT_ATTRIBUTION') {
          const key = compositeProfile + '::composite';
          if (!profileMap.has(key)) {
            profileMap.set(key, {name: compositeProfile, type: 'composto', rows: [], risk: false, master: false, derived: false});
          }
          profileMap.get(key)!.rows.push(row);
        }
      }
    }
    
    return Array.from(profileMap.values());
  }, [mapping]);

  const consolidatedSimpleProfiles = useMemo(() => {
    if (!mapping?.simpleProfilesCatalog) return [];
    return mapping.simpleProfilesCatalog;
  }, [mapping]);

  const visibleSimpleProfiles = useMemo(() => {
    // Do not show derived-only profiles in the simple card; keep masters and regular simple profiles
    return consolidatedSimpleProfiles.filter((p) => !(p.derived && !p.master));
  }, [consolidatedSimpleProfiles]);

  const cardProgress = useMemo(() => {
    if (!mapping) {
      return { simple: 0, composite: 0, users: 0, overall: 0 };
    }

    const mappedSimpleSet = new Set(
      assignedProfiles
        .filter((p) => p.type === 'simples')
        .map((p) => String(p.name || '').trim().toUpperCase())
    );
    const simpleTotal = visibleSimpleProfiles.length;
    const simpleCovered = visibleSimpleProfiles.filter((p) => mappedSimpleSet.has(String(p.name || '').trim().toUpperCase())).length;
    const simple = simpleTotal > 0 ? Math.round((simpleCovered / simpleTotal) * 100) : 0;

    const assignedCompositeSet = new Set(
      assignedProfiles
        .filter((p) => p.type === 'composto')
        .map((p) => String(p.name || '').trim().toUpperCase())
    );
    const compositeCatalog = (mapping.compositeProfilesCatalog || []).map((n) => String(n || '').trim().toUpperCase()).filter(Boolean);
    const compositeTotal = compositeCatalog.length > 0 ? compositeCatalog.length : kpis.uniqueCompositeProfiles;
    const compositeCovered = compositeCatalog.length > 0
      ? compositeCatalog.filter((name) => assignedCompositeSet.has(name)).length
      : assignedCompositeSet.size;
    const composite = compositeTotal > 0 ? Math.round((compositeCovered / compositeTotal) * 100) : 0;

    const users = kpis.progressPercent;
    const overall = kpis.progressPercent;

    return { simple, composite, users, overall };
  }, [mapping, assignedProfiles, visibleSimpleProfiles, kpis]);

  const usersValidationView = useMemo(() => {
    if (!mapping) return [] as Array<{
      user: string;
      userName: string;
      hasMapping: boolean;
      simpleCount: number;
      compositeCount: number;
      totalProfiles: number;
    }>;

    const usersMap = new Map<string, {
      user: string;
      userName: string;
      hasMapping: boolean;
      simpleSet: Set<string>;
      compositeSet: Set<string>;
    }>();

    for (const responsibleUser of mapping.responsibleUsers || []) {
      const user = String(responsibleUser || '').trim();
      if (!user) continue;
      const key = user.toUpperCase();
      usersMap.set(key, {
        user,
        userName: user,
        hasMapping: false,
        simpleSet: new Set<string>(),
        compositeSet: new Set<string>(),
      });
    }

    for (const row of mapping.items) {
      const user = readField(row, USER_FIELD_CANDIDATES);
      if (!user) continue;
      const key = user.toUpperCase();
      if (!usersMap.has(key)) {
        usersMap.set(key, {
          user,
          userName: user,
          hasMapping: false,
          simpleSet: new Set<string>(),
          compositeSet: new Set<string>(),
        });
      }

      const entry = usersMap.get(key)!;
      entry.hasMapping = true;

      const userName = readField(row, USER_NAME_FIELD_CANDIDATES);
      if (userName) entry.userName = userName;

      const simpleProfile = readField(row, PROFILE_SIMPLE_FIELD_CANDIDATES);
      if (simpleProfile) entry.simpleSet.add(simpleProfile.toUpperCase());

      const compositeProfile = readField(row, PROFILE_COMPOSITE_FIELD_CANDIDATES);
      if (compositeProfile && compositeProfile.toUpperCase() !== 'DIRECT_ATTRIBUTION') {
        entry.compositeSet.add(compositeProfile.toUpperCase());
      }
    }

    return Array.from(usersMap.values())
      .map((entry) => {
        const simpleCount = entry.simpleSet.size;
        const compositeCount = entry.compositeSet.size;
        return {
          user: entry.user,
          userName: entry.userName,
          hasMapping: entry.hasMapping,
          simpleCount,
          compositeCount,
          totalProfiles: simpleCount + compositeCount,
        };
      })
      .sort((a, b) => {
        if (a.hasMapping !== b.hasMapping) return a.hasMapping ? -1 : 1;
        return a.user.localeCompare(b.user);
      });
  }, [mapping]);

  // ── Render helpers ──────────────────────────────────────────────────────────

  if (cycleLoading) {
    return (
      <div className="adsp-root">
        <div className="adsp-navbar">
          <div className="adsp-navbar-logo">
            <img src="/assets/img/icons/logo_new.svg" alt="TrustSIS" />
          </div>
          <span className="adsp-navbar-brand">Access Design Solution</span>
        </div>
        <div className="adsp-login-wrap text-center">
          <div className="adsp-loading">Carregando...</div>
        </div>
      </div>
    );
  }

  if (cycleError || !cycleInfo) {
    return (
      <div className="adsp-root">
        <div className="adsp-navbar">
          <div className="adsp-navbar-logo">
            <img src="/assets/img/icons/logo_new.svg" alt="TrustSIS" />
          </div>
          <span className="adsp-navbar-brand">Access Design Solution</span>
        </div>
        <div className="adsp-login-wrap">
          <div className="adsp-login-card text-center">
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🔍</div>
            <p className="adsp-status-title fw-bold">Portal não encontrado</p>
            <p className="adsp-status-note">{cycleError || 'Verifique o link recebido.'}</p>
          </div>
        </div>
      </div>
    );
  }

  if (cycleInfo.status === 'closed') {
    return (
      <div className="adsp-root">
        <div className="adsp-navbar">
          <div className="adsp-navbar-logo">
            <img src="/assets/img/icons/logo_new.svg" alt="TrustSIS" />
          </div>
          <span className="adsp-navbar-brand">Access Design Solution</span>
          <div className="adsp-navbar-user ms-auto">
            <div className="adsp-navbar-user-client">{cycleInfo.clientName}</div>
          </div>
        </div>
        <div className="adsp-login-wrap">
          <div className="adsp-login-card text-center">
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🔒</div>
            <p className="adsp-status-title fw-bold">Este ciclo foi encerrado</p>
            <p className="adsp-status-note">O acesso ao portal não está mais disponível.</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Login screen ────────────────────────────────────────────────────────────

  if (!session) {
    return (
      <div className="adsp-root">
        <div className="adsp-navbar">
          <div className="adsp-navbar-logo">
            <img src="/assets/img/icons/logo_new.svg" alt="TrustSIS" />
          </div>
          <span className="adsp-navbar-brand">Access Design Solution</span>
          <div className="adsp-navbar-user ms-auto">
            <div className="adsp-navbar-user-client">{cycleInfo.clientName}</div>
          </div>
        </div>
        <div className="adsp-login-wrap">
          <div className="adsp-login-card">
            <div className="adsp-login-title">Access Design Solution</div>
            <div className="adsp-login-sub">
              {cycleInfo.name}
            </div>
            <div className="adsp-login-client-name">{cycleInfo.clientName}</div>
            {cycleInfo.clientLogo && (
              <div className="adsp-login-client-logo-wrap">
                <img src={cycleInfo.clientLogo} alt={`Logo ${cycleInfo.clientName}`} className="adsp-login-client-logo" />
              </div>
            )}

            {loginError && <div className="adsp-error">{loginError}</div>}

            <form onSubmit={handleLogin}>
              <div className="mb-3">
                <label className="adsp-login-label">Seu ID de acesso</label>
                <input
                  type="text"
                  className="form-control adsp-login-input"
                  placeholder="ex.: JSILVA"
                  autoComplete="username"
                  value={loginForm.keyUserId}
                  onChange={(e) => setLoginForm((p) => ({ ...p, keyUserId: e.target.value.trim() }))}
                  disabled={loginLoading}
                />
              </div>
              <div className="mb-4">
                <label className="adsp-login-label">Senha</label>
                <input
                  type="password"
                  className="form-control adsp-login-input"
                  placeholder="Sua senha"
                  autoComplete="current-password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm((p) => ({ ...p, password: e.target.value }))}
                  disabled={loginLoading}
                />
              </div>
              <button className="adsp-login-btn" type="submit" disabled={loginLoading}>
                {loginLoading ? 'Entrando…' : 'Entrar'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── Dashboard screen ────────────────────────────────────────────────────────

  return (
    <div className="adsp-root">
      <div className="adsp-navbar">
        <div className="adsp-navbar-logo">
          <img src="/assets/img/icons/logo_new.svg" alt="TrustSIS" />
        </div>
        <span className="adsp-navbar-brand adsp-navbar-brand-desktop">Access Design Solution</span>
        <span className="adsp-navbar-brand adsp-navbar-brand-mobile">ADS</span>
        <div className="adsp-navbar-user ms-auto">
          <div className="adsp-navbar-user-info">
            <div className="adsp-navbar-user-name">{session.keyUserName}</div>
            <div className="adsp-navbar-user-client">{cycleInfo.clientName}</div>
          </div>
          <button className="adsp-navbar-logout-btn" onClick={handleLogout} title="Sair">
            ✕
          </button>
        </div>
      </div>

      <div className="adsp-dash">
        {mappingLoading && (
          <div className="text-center adsp-loading">
            <div className="spinner-border spinner-border-sm me-2" role="status" />
            Carregando mapeamento…
          </div>
        )}

        {mappingError && (
          <div className="adsp-error" style={{ maxWidth: '600px' }}>
            {mappingError}
          </div>
        )}

        {mapping && !mappingLoading && (
          <div className="adsp-dashboard-layout">
            <aside className="adsp-side-panel">
              <div className="adsp-side-title">
                <BsBarChartFill className="adsp-kpi-label-icon" />
                Visão geral
              </div>

              <div className="adsp-side-user-block">
                <div className="adsp-side-user-photos">
                  {mapping.cycleInfo.clientLogo && (
                    <div className="adsp-side-client-logo-wrap">
                      <img src={mapping.cycleInfo.clientLogo} alt={mapping.cycleInfo.clientName} className="adsp-side-client-logo" />
                    </div>
                  )}
                  <div className="adsp-side-avatar-wrap">
                    <div className="adsp-side-avatar">
                      <svg width="48" height="48" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="19" cy="19" r="19" fill="#5b67f5"/>
                        <path d="M19 20.5c3.59 0 6.5-2.91 6.5-6.5S22.59 7.5 19 7.5 12.5 10.41 12.5 14s2.91 6.5 6.5 6.5zm0 2.5c-4.14 0-12.5 2.08-12.5 6.25V32h25v-2.75c0-4.17-8.36-6.25-12.5-6.25z" fill="#fff"/>
                      </svg>
                    </div>
                    <div className="adsp-side-role">Key User</div>
                  </div>
                </div>
                <div className="adsp-side-name">Bem vindo, {session.keyUserName}</div>
                <div className="adsp-side-meta">{mapping.cycleInfo.clientName}</div>
              </div>

              <div className="adsp-side-divider" />

              <div className="adsp-side-ring" style={{ background: `conic-gradient(#5b67f5 ${kpis.progressPercent}%, #dfe3ef ${kpis.progressPercent}% 100%)` }}>
                <div className="adsp-side-ring-inner">
                  <div className="adsp-side-ring-value">{kpis.progressPercent}%</div>
                  <div className="adsp-side-ring-label">progresso geral</div>
                </div>
              </div>

              <div className="adsp-side-divider" />

              <div className="adsp-side-bars">
                <div className="adsp-side-bar-col">
                  <div className="adsp-side-bar-track">
                    <div className="adsp-side-bar-fill" style={{ height: `${dashboardStats.engagementPercent}%` }} />
                  </div>
                  <div className="adsp-side-bar-value">{dashboardStats.engagementPercent}%</div>
                  <div className="adsp-side-bar-label">Engajamento</div>
                </div>
                <div className="adsp-side-bar-col">
                  <div className="adsp-side-bar-track">
                    <div className="adsp-side-bar-fill" style={{ height: `${dashboardStats.executionPercent}%` }} />
                  </div>
                  <div className="adsp-side-bar-value">{dashboardStats.executionPercent}%</div>
                  <div className="adsp-side-bar-label">Execução</div>
                </div>
                <div className="adsp-side-bar-col">
                  <div className="adsp-side-bar-track">
                    <div className="adsp-side-bar-fill" style={{ height: `${dashboardStats.completionPercent}%` }} />
                  </div>
                  <div className="adsp-side-bar-value">{dashboardStats.completionPercent}%</div>
                  <div className="adsp-side-bar-label">Conclusão</div>
                </div>
              </div>
            </aside>

            <section className="adsp-main-panel">
              <div className="adsp-kpi-grid">
                {/* Card 1 — Perfis Simples */}
                <div className="adsp-kpi-card adsp-kpi-card-profiles">
                  <div className="adsp-kpi-head">
                    <div className="adsp-kpi-label">
                      <BsKeyFill className="adsp-kpi-label-icon" />
                      Perfis Simples
                    </div>
                    <div className="adsp-kpi-donut" style={{ background: `conic-gradient(#5b67f5 ${cardProgress.simple}%, #dfe3ef ${cardProgress.simple}% 100%)` }}>
                      <div className="adsp-kpi-donut-inner">{cardProgress.simple}%</div>
                    </div>
                  </div>
                  <div className="adsp-kpi-subtitle">Perfis simples para validação</div>
                  <div className="adsp-kpi-value">{visibleSimpleProfiles.length || kpis.uniqueSimpleProfiles}</div>
                  {visibleSimpleProfiles.length > 0 && (
                    <div className="adsp-profiles-inner">
                      <div className="adsp-profiles-inner-cards">
                        {visibleSimpleProfiles.map((profile, idx) => {
                          const basis = String(profile.basis || '').toLowerCase();
                          const isActivityBased = basis === 'atividade';
                          const hasExtraType = !!(profile.risk || profile.master || profile.derived);
                          const area = String(profile.area || '').trim();
                          const n1 = isActivityBased ? String(profile.process || '').trim() : String(profile.n1 || '').trim();
                          const n2 = isActivityBased ? String(profile.subprocess || '').trim() : String(profile.n2 || '').trim();
                          const n3 = isActivityBased ? String(profile.activity || '').trim() : String(profile.n3 || '').trim();
                          const n4 = !isActivityBased ? String(profile.n4 || '').trim() : '';
                          return (
                            <div key={idx} className="adsp-profile-card">
                              <div className="adsp-profile-header">
                                <div className="adsp-profile-name">
                                  <BsKeyFill className="adsp-profile-icon" />
                                  {profile.name}
                                </div>
                                <div className={hasExtraType ? 'adsp-profile-tag-row' : 'adsp-profile-tag-stack'}>
                                  <span className="adsp-profile-badge adsp-profile-badge-simples">Simples</span>
                                  {profile.master && <span className="adsp-profile-badge adsp-profile-badge-master">Mestre</span>}
                                  {profile.risk && <span className="adsp-profile-badge adsp-profile-badge-risk">Risco</span>}
                                  {profile.derived && <span className="adsp-profile-badge adsp-profile-badge-derived">Derivado</span>}
                                </div>
                              </div>
                              <div className="adsp-profile-details">
                                <div className="adsp-profile-row"><span className="adsp-profile-key">Base:</span><span className="adsp-profile-value">{isActivityBased ? 'Atividade' : 'Posição'}</span></div>
                                {area && <div className="adsp-profile-row"><span className="adsp-profile-key">Área:</span><span className="adsp-profile-value">{area}</span></div>}
                                {n1 && <div className="adsp-profile-row"><span className="adsp-profile-key">{isActivityBased ? 'Processo:' : 'N1:'}</span><span className="adsp-profile-value">{n1}</span></div>}
                                {n2 && <div className="adsp-profile-row"><span className="adsp-profile-key">{isActivityBased ? 'Subprocesso:' : 'N2:'}</span><span className="adsp-profile-value">{n2}</span></div>}
                                {n3 && <div className="adsp-profile-row"><span className="adsp-profile-key">{isActivityBased ? 'Atividade:' : 'N3:'}</span><span className="adsp-profile-value">{n3}</span></div>}
                                {n4 && <div className="adsp-profile-row"><span className="adsp-profile-key">N4:</span><span className="adsp-profile-value">{n4}</span></div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Card 2 — Perfis Compostos */}
                <div className="adsp-kpi-card adsp-kpi-card-profiles">
                  <div className="adsp-kpi-head">
                    <div className="adsp-kpi-label">
                      <BsKey className="adsp-kpi-label-icon" />
                      Perfis Compostos
                    </div>
                    <div className="adsp-kpi-donut" style={{ background: `conic-gradient(#5b67f5 ${cardProgress.composite}%, #dfe3ef ${cardProgress.composite}% 100%)` }}>
                      <div className="adsp-kpi-donut-inner">{cardProgress.composite}%</div>
                    </div>
                  </div>
                  <div className="adsp-kpi-subtitle">Perfis compostos para validação</div>
                  <div className="adsp-kpi-value">{kpis.uniqueCompositeProfiles}</div>
                  {assignedProfiles.filter(p => p.type === 'composto').length > 0 && (
                    <div className="adsp-profiles-inner">
                      <div className="adsp-profiles-inner-cards">
                        {assignedProfiles.filter(p => p.type === 'composto').map((profile, idx) => {
                          const firstRow = profile.rows[0] || {};
                          const n1 = readField(firstRow, ['n1', 'N1']);
                          const n2 = readField(firstRow, ['n2', 'N2']);
                          const n3 = readField(firstRow, ['n3', 'N3']);
                          const n4 = readField(firstRow, ['n4', 'N4']);
                          return (
                            <div key={idx} className="adsp-profile-card">
                              <div className="adsp-profile-header">
                                <div className="adsp-profile-name">
                                  <BsKey className="adsp-profile-icon" />
                                  {profile.name}
                                </div>
                                <span className="adsp-profile-badge adsp-profile-badge-composto">Composto</span>
                              </div>
                              <div className="adsp-profile-details">
                                {n1 && <div className="adsp-profile-row"><span className="adsp-profile-key">N1:</span><span className="adsp-profile-value">{n1}</span></div>}
                                {n2 && <div className="adsp-profile-row"><span className="adsp-profile-key">N2:</span><span className="adsp-profile-value">{n2}</span></div>}
                                {n3 && <div className="adsp-profile-row"><span className="adsp-profile-key">N3:</span><span className="adsp-profile-value">{n3}</span></div>}
                                {n4 && <div className="adsp-profile-row"><span className="adsp-profile-key">N4:</span><span className="adsp-profile-value">{n4}</span></div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Card 3 — Usuários */}
                <div className="adsp-kpi-card">
                  <div className="adsp-kpi-head">
                    <div className="adsp-kpi-label">
                      <BsPersonFill className="adsp-kpi-label-icon" />
                      Usuários para validação
                    </div>
                    <div className="adsp-kpi-donut" style={{ background: `conic-gradient(#5b67f5 ${cardProgress.users}%, #dfe3ef ${cardProgress.users}% 100%)` }}>
                      <div className="adsp-kpi-donut-inner">{cardProgress.users}%</div>
                    </div>
                  </div>
                  <div className="adsp-kpi-subtitle">Usuários vinculados ao key user</div>
                  <div className="adsp-kpi-value">{kpis.usersForValidation}</div>
                  <div className="adsp-kpi-meta">Pendentes: <strong>{dashboardStats.pendingUsers}</strong> · Com mapeamento: <strong>{kpis.usersWithMapping}</strong></div>
                  {usersValidationView.length > 0 && (
                    <div className="adsp-users-inner">
                      <div className="adsp-users-inner-cards">
                        {usersValidationView.map((entry) => (
                          <div key={entry.user} className="adsp-user-validation-card">
                            <div className="adsp-user-validation-head">
                              <div className="adsp-user-validation-user">{entry.user}</div>
                              <span className={`adsp-profile-badge ${entry.hasMapping ? 'adsp-profile-badge-simples' : 'adsp-profile-badge-risk'}`}>
                                {entry.hasMapping ? 'Com mapeamento' : 'Pendente'}
                              </span>
                            </div>
                            <div className="adsp-user-validation-name">{entry.userName}</div>
                            <div className="adsp-user-validation-metrics">
                              <span>Simples: <strong>{entry.simpleCount}</strong></span>
                              <span>Compostos: <strong>{entry.compositeCount}</strong></span>
                              <span>Total: <strong>{entry.totalProfiles}</strong></span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Card 4 — Progresso geral */}
                <div className="adsp-kpi-card adsp-kpi-card-progress">
                  <div className="adsp-kpi-head">
                    <div className="adsp-kpi-label">
                      <BsGraphUpArrow className="adsp-kpi-label-icon" />
                      Progresso geral
                    </div>
                    <div className="adsp-kpi-donut" style={{ background: `conic-gradient(#5b67f5 ${cardProgress.overall}%, #dfe3ef ${cardProgress.overall}% 100%)` }}>
                      <div className="adsp-kpi-donut-inner">{cardProgress.overall}%</div>
                    </div>
                  </div>
                  <div className="adsp-kpi-subtitle">Avanço geral da validação do key user</div>
                  <div className="adsp-kpi-value">{kpis.progressPercent}%</div>
                  <div className="adsp-kpi-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={kpis.progressPercent}>
                    <div className="adsp-kpi-progress-fill" style={{ width: `${kpis.progressPercent}%` }} />
                  </div>
                  <div className="adsp-kpi-meta">Cobertura da carteira de usuários atribuída ao key user</div>
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default ADSPortal;
